import { useState, useEffect, useCallback, useRef } from 'react';
import { ref, onValue, set, push, get } from 'firebase/database';
import { db } from '../services/firebase';
import { User } from 'firebase/auth';

// ---------- types ----------

export type TournamentStatus = 'pending' | 'active' | 'finished';

export interface TournamentPlayer {
  uid: string;
  displayName: string;
  points: number;
  joinedAt: number;
}

export interface Tournament {
  id: string;
  name: string;
  hostUid: string;
  hostName: string;
  inviteCode: string;
  durationDays: number;
  status: TournamentStatus;
  createdAt: number;
  startsAt: number;        // timestamp when tournament begins
  endsAt: number;          // timestamp when tournament ends
  players: Record<string, TournamentPlayer>;
  weeklyPoints: Record<string, number>; // uid -> points for the current week
}

export interface UseTournamentReturn {
  weeklyPoints: number;
  privateTournaments: Tournament[];
  loading: boolean;
  addPoints: (outcome: 'win' | 'draw' | 'loss') => Promise<void>;
  ensureWeeklyInit: () => Promise<void>;
  createPrivateTournament: (name: string, durationDays: number) => Promise<string | null>;
  joinPrivateTournament: (inviteCode: string) => Promise<boolean>;
}

// ---------- helpers ----------

function getWeekKey(date: Date = new Date()): string {
  // ISO week: YYYY-Www
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function getTodayString(): string {
  return new Date().toISOString().split('T')[0];
}

function generateInviteCode(): string {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}

function getLocalWeeklyPoints(): number {
  try {
    const raw = localStorage.getItem('sbarazzo_weekly_points');
    const data = raw ? JSON.parse(raw) : {};
    return data.points || 0;
  } catch { return 0; }
}

function setLocalWeeklyPoints(points: number, weekKey: string) {
  try {
    localStorage.setItem('sbarazzo_weekly_points', JSON.stringify({ points, weekKey }));
  } catch { /* ignore */ }
}

function getLocalPrivateTournaments(): Tournament[] {
  try {
    const raw = localStorage.getItem('sbarazzo_private_tournaments');
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function setLocalPrivateTournaments(tournaments: Tournament[]) {
  try {
    localStorage.setItem('sbarazzo_private_tournaments', JSON.stringify(tournaments));
  } catch { /* ignore */ }
}

const POINTS_MAP: Record<string, number> = {
  win: 3,
  draw: 1,
  loss: 0,
};

// ---------- hook ----------

export function useTournament(user: User | null): UseTournamentReturn {
  const [weeklyPoints, setWeeklyPoints] = useState<number>(getLocalWeeklyPoints);
  const [privateTournaments, setPrivateTournaments] = useState<Tournament[]>(getLocalPrivateTournaments);
  const [loading, setLoading] = useState(true);

  // Track whether we've fully initialized from Firebase
  const fbInitialized = useRef(false);

  // ---------- weekly points sync ----------

  const syncWeeklyPointsToFirebase = useCallback(async (points: number, weekKey: string, uid: string) => {
    try {
      await set(ref(db, `users/${uid}/tournament/weekly/${weekKey}`), { points });
    } catch (e) {
      console.error('[useTournament] Failed to sync weekly points:', e);
    }
  }, []);

  const loadWeeklyPoints = useCallback((currentWeekKey: string) => {
    const local = (() => {
      try {
        const raw = localStorage.getItem('sbarazzo_weekly_points');
        return raw ? JSON.parse(raw) : null;
      } catch { return null; }
    })();

    if (local && local.weekKey === currentWeekKey) {
      setWeeklyPoints(local.points || 0);
    } else {
      // New week — reset
      setWeeklyPoints(0);
      setLocalWeeklyPoints(0, currentWeekKey);
    }
  }, []);

  // ---------- private tournaments sync ----------

  const syncPrivateTournaments = useCallback((list: Tournament[]) => {
    setPrivateTournaments(list);
    setLocalPrivateTournaments(list);
  }, []);

  // ---------- main effect ----------

  useEffect(() => {
    if (fbInitialized.current) return;
    fbInitialized.current = true;

    const weekKey = getWeekKey();

    if (!user) {
      loadWeeklyPoints(weekKey);
      setPrivateTournaments(getLocalPrivateTournaments());
      setLoading(false);
      return;
    }

    // Listen to weekly points
    const weeklyRef = ref(db, `users/${user.uid}/tournament/weekly/${weekKey}`);
    const unsubWeekly = onValue(
      weeklyRef,
      (snapshot) => {
        const val = snapshot.val();
        const pts = val?.points ?? getLocalWeeklyPoints();
        setWeeklyPoints(pts);
        setLocalWeeklyPoints(pts, weekKey);
      },
      () => {
        // Firebase error — fall back to local
        const local = getLocalWeeklyPoints();
        setWeeklyPoints(local);
      }
    );

    // Listen to private tournaments
    const tournamentsRef = ref(db, `users/${user.uid}/tournament/privateTournaments`);
    const unsubTournaments = onValue(
      tournamentsRef,
      (snapshot) => {
        const fb = snapshot.val();
        if (fb) {
          const list: Tournament[] = Object.entries(fb).map(([id, data]) => ({
            id,
            ...(data as Omit<Tournament, 'id'>),
          }));
          syncPrivateTournaments(list);
        } else {
          syncPrivateTournaments([]);
        }
        setLoading(false);
      },
      () => {
        syncPrivateTournaments(getLocalPrivateTournaments());
        setLoading(false);
      }
    );

    return () => {
      unsubWeekly();
      unsubTournaments();
    };
  }, [user, loadWeeklyPoints, syncPrivateTournaments]);

  // ---------- addPoints ----------

  const addPoints = useCallback(
    async (outcome: 'win' | 'draw' | 'loss') => {
      const pts = POINTS_MAP[outcome] ?? 0;
      if (pts === 0) return;

      const weekKey = getWeekKey();
      const newTotal = weeklyPoints + pts;

      setWeeklyPoints(newTotal);
      setLocalWeeklyPoints(newTotal, weekKey);

      if (user) {
        await syncWeeklyPointsToFirebase(newTotal, weekKey, user.uid);
      }
    },
    [weeklyPoints, user, syncWeeklyPointsToFirebase]
  );

  // ---------- ensureWeeklyInit ----------

  const ensureWeeklyInit = useCallback(async () => {
    const weekKey = getWeekKey();
    const local = (() => {
      try {
        const raw = localStorage.getItem('sbarazzo_weekly_points');
        return raw ? JSON.parse(raw) : null;
      } catch { return null; }
    })();

    if (!local || local.weekKey !== weekKey) {
      // New week — reset
      setWeeklyPoints(0);
      setLocalWeeklyPoints(0, weekKey);
      if (user) {
        await syncWeeklyPointsToFirebase(0, weekKey, user.uid);
      }
    }
  }, [user, syncWeeklyPointsToFirebase]);

  // ---------- createPrivateTournament ----------

  const createPrivateTournament = useCallback(
    async (name: string, durationDays: number): Promise<string | null> => {
      if (!user) return null;
      const inviteCode = generateInviteCode();
      const now = Date.now();
      const startsAt = now;
      const endsAt = now + durationDays * 86400000;

      const newTournament: Omit<Tournament, 'id'> = {
        name,
        hostUid: user.uid,
        hostName: user.displayName ?? 'Host',
        inviteCode,
        durationDays,
        status: 'pending',
        createdAt: now,
        startsAt,
        endsAt,
        players: {
          [user.uid]: {
            uid: user.uid,
            displayName: user.displayName ?? 'Host',
            points: 0,
            joinedAt: now,
          },
        },
        weeklyPoints: {},
      };

      try {
        const tournamentsRef = ref(db, `users/${user.uid}/tournament/privateTournaments`);
        const newRef = push(tournamentsRef);
        const tournamentId = newRef.key;
        if (!tournamentId) return null;

        // Write tournament data
        await set(newRef, newTournament);

        // Also write a global invite-code index so friends can find this tournament
        const globalIndexRef = ref(db, `tournaments/byCode/${inviteCode}`);
        await set(globalIndexRef, {
          tournamentId,
          hostUid: user.uid,
          hostName: user.displayName ?? 'Host',
          name,
          status: 'pending',
          createdAt: now,
          endsAt,
        });

        return tournamentId;
      } catch (e) {
        console.error('[useTournament] createPrivateTournament error:', e);
        return null;
      }
    },
    [user]
  );

  // ---------- joinPrivateTournament ----------

  const joinPrivateTournament = useCallback(
    async (inviteCode: string): Promise<boolean> => {
      if (!user) return false;

      try {
        // Look up the tournament via the global invite-code index
        const globalIndexRef = ref(db, `tournaments/byCode/${inviteCode.toUpperCase()}`);
        const indexSnapshot = await get(globalIndexRef);

        if (!indexSnapshot.exists()) return false;

        const indexData = indexSnapshot.val() as {
          tournamentId: string;
          hostUid: string;
        };

        // Read the actual tournament data from the host's node
        const tournamentRef = ref(
          db,
          `users/${indexData.hostUid}/tournament/privateTournaments/${indexData.tournamentId}`
        );
        const snapshot = await get(tournamentRef);

        if (!snapshot.exists()) return false;

        const foundTournament = snapshot.val() as Omit<Tournament, 'id'>;

        if (foundTournament.players[user.uid]) return true; // already joined

        const now = Date.now();
        const updatedPlayers: Record<string, TournamentPlayer> = {
          ...foundTournament.players,
          [user.uid]: {
            uid: user.uid,
            displayName: user.displayName ?? 'Guest',
            points: 0,
            joinedAt: now,
          },
        };

        const updatedTournament: Omit<Tournament, 'id'> = {
          ...foundTournament,
          players: updatedPlayers,
          status: 'active',
        };

        await set(tournamentRef, updatedTournament);
        return true;
      } catch (e) {
        console.error('[useTournament] joinPrivateTournament error:', e);
        return false;
      }
    },
    [user]
  );

  return {
    weeklyPoints,
    privateTournaments,
    loading,
    addPoints,
    ensureWeeklyInit,
    createPrivateTournament,
    joinPrivateTournament,
  };
}